import { FC, useCallback, useEffect, useState } from "react";
import { TrashIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateErrorNotificationEmailDto,
  ErrorNotificationEmailQueryKey,
  createErrorNotificationEmail,
  deleteErrorNotificationEmail,
  getErrorNotificationEmails,
} from "api/errorNotificationEmail.api";
import PrimaryButton from "components/buttons/PrimaryButton";
import Modal from "components/Modal";
import { ModalContentWrapper } from "components/ModalContentWrapper";
import { TextInputFormProvider } from "components/TextInputFormProvider";
import { useNotification } from "hooks/useNotification";
import { FormProvider, useForm } from "react-hook-form";

export const NaturvardsverketNotificationsSection: FC = () => {
  const {
    data: emails,
    isLoading,
    isError,
  } = useQuery(getErrorNotificationEmails());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const notification = useNotification();

  const createMutation = useMutation({
    ...createErrorNotificationEmail(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ErrorNotificationEmailQueryKey.GetList],
      });
      notification.info("E-postadress tillagd");
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });

  const deleteMutation = useMutation({
    ...deleteErrorNotificationEmail(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ErrorNotificationEmailQueryKey.GetList],
      });
      notification.info("E-postadress borttagen");
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });

  const form = useForm<CreateErrorNotificationEmailDto>();

  const onSubmit = form.handleSubmit((data: CreateErrorNotificationEmailDto) =>
    createMutation.mutate(data),
  );

  const onModalClose = useCallback(() => {
    setIsAddModalOpen(false);
    setTimeout(() => form.reset(), 300);
  }, [form]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      onModalClose();
    }
  }, [createMutation.isSuccess, onModalClose]);

  useEffect(() => {
    if (isError) {
      notification.error("Kunde inte hämta e-postadresser, försök igen senare");
    }
  }, [isError, notification]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-prose">
            <h4 className="text-sm font-semibold text-slate-900">
              Felnotifieringar
            </h4>
            <p className="mt-1 text-sm text-slate-500">
              E-postadresser som får besked när något går fel vid automatisk
              rapportering till Naturvårdsverket.
            </p>
          </div>
          <PrimaryButton size="small" onClick={() => setIsAddModalOpen(true)}>
            Lägg till e-postadress
          </PrimaryButton>
        </div>

        <div className="mt-4">
          {isError ? (
            <p className="rounded-lg border border-dashed border-rose-200 bg-rose-50/60 px-4 py-6 text-center text-sm text-rose-700">
              Kunde inte hämta e-postadresser. Försök igen senare.
            </p>
          ) : emails && emails.length > 0 ? (
            <ul className="divide-y divide-slate-100 overflow-hidden rounded-lg ring-1 ring-inset ring-slate-200">
              {emails.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 bg-white px-4 py-3"
                >
                  <span className="flex items-center gap-2.5 text-sm text-slate-900">
                    <EnvelopeIcon className="h-4 w-4 text-slate-400" />
                    {item.email}
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(item.id)}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Ta bort
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-4 py-6 text-center text-sm text-slate-500">
              Inga e-postadresser tillagda än.
            </p>
          )}
        </div>
      </div>

      <Modal
        isOpen={isAddModalOpen}
        onClose={onModalClose}
        title="Lägg till e-postadress"
        actionText="Lägg till"
        actionHandler={onSubmit}
        isLoading={createMutation.isPending}
      >
        <ModalContentWrapper>
          <FormProvider {...form}>
            <TextInputFormProvider<CreateErrorNotificationEmailDto>
              label="E-postadress"
              name="email"
              required
            />
          </FormProvider>
        </ModalContentWrapper>
      </Modal>
    </>
  );
};
